/* Level and challenge definitions shared by the four training modules. */
import { rng as seededRng } from '../core/dsp/rng';
import type { ModuleId } from '../core/store';
import { dateKey, hashStr, type Rng } from '../core/util';
import type { ExerciseConfig, InputKind, Question, SessionStats } from './exercise/types';
import type { LevelCardItem } from './module/ModulePage';

export interface LevelDef extends LevelCardItem {
  n: number;
  title: string;
  desc: string;
  /** progress key and route id, e.g. "nota.egz.sap.2" */
  key: string;
  route: string;
}

export type ChallengeConfig = Omit<Partial<ExerciseConfig>, 'make'> & {
  make: (i: number, st: SessionStats, rng: Rng) => Question;
  /** same questions for everyone on a given day */
  seeded?: boolean;
};

export interface ChallengeDef {
  id: string;
  title: string;
  desc: string;
  badge: string;
  key: string;
  route: string;
  input?: InputKind;
  cfg: ChallengeConfig;
}

export interface ModuleDefs {
  levels: LevelDef[];
  challenges: ChallengeDef[];
}

export const challengeCards = (list: readonly ChallengeDef[]): LevelCardItem[] =>
  list.map((c) => ({ key: c.key, route: c.route, title: c.title, desc: c.desc, tag: c.badge, challenge: true }));

/** Exercise config for a challenge; seeded challenges get today's deterministic RNG. */
export function challengeConfig(module: ModuleId, ch: ChallengeDef): ExerciseConfig {
  const rng: Rng = ch.cfg.seeded ? seededRng(hashStr(`gunun-${dateKey()}`)) : Math.random;
  const { seeded: _seeded, make, ...cfg } = ch.cfg;
  return {
    key: ch.key,
    module,
    back: `${module}.meydan`,
    title: ch.title,
    desc: ch.desc,
    mode: 'challenge',
    input: ch.input,
    ...cfg,
    make: (i, st) => make(i, st, rng),
  };
}
